import useNovelNovemberSprint from "src/hooks/useNovelNovemberSprint";
import { insertDraftStreamContent } from "../../../../lib/draftInsertion";
import { forwardRef, useMemo, useRef, useState } from "react";
import { AppEventPublisher } from "src/AppEventPublisher";
import Auth from "src/Auth";
import {
  initializeComparisonMode,
  persistComparisonInvocationId,
  handleComparisonChunk,
  handleNormalChunk,
  handleComparisonEnd,
  handleComparisonAbortError,
} from "./comparisonChunkHandlers.ts";
import {
  createBenchmarkInterceptContext,
  maybeInterceptBenchmarkChunk,
  benchmarkStreamEnded,
  benchmarkStreamFailed,
} from "src/components/BenchmarkTakeover/benchmarkIntercept";
import { getAirTableSupportUrl } from "src/components/HelpMenu";
import ModelOutageMessage from "src/components/ModelOutageMessage";
import { getTextFromTiptapListField } from "src/components/TiptapListField";
import { useToast } from "src/components/ToastWrapper/index.js";
import XStateConditionalRenderer from "src/components/XStateConditionalRenderer";
import { useCharacterNames } from "src/hooks/useCharacterNames";
import { getSprintErrorMessage, isProbableModelOutageError } from "src/lib/modelOutage";
import { useProjectStore } from "src/stores/projectStore";
import { useStoryBibleStore } from "src/stores/storyBibleStore";
import { useSudowriteStore } from "src/stores/sudowriteStore";
import { themeVariables } from "src/themeVariables";
import { countWords, parseCommands, sendInvocationEvent } from "src/Utils";
import styled from "styled-components";
import { isAutoPOV, isAutoTense, POVS, DEFAULT_POV_TENSE_OBJECT } from "../../../../../../src/shared/constants";
import { useStructuredOutlineStore } from "../../../StoryBible/StructuredOutline/StructuredOutlineStore";
import { convertQuotes } from "../../../../lib/convertQuotes";
import {
  clearPendingStreamChunkBoundary,
  splitStreamChunkLines,
} from "../../Utils";
import { MarkdownStreamer } from "../../markdownStreamer";
import { DraftStates } from "../draftStateMachine";
import PreflightAnalysis from "../PreflightAnalysis";
import { persistDraftPreflightSnapshot, clearDraftPreflightSnapshot } from "../draftPreflightPersistence";
import PanelButtons from "./PanelButtons";
import { POVTenseButtons } from "./POVTenseButtons";
import { TourSignal, AnchorIds } from "../../../StickyTour/TourTypes";
import { useStickyTourStore } from "../../../../stores/stickyTourStore";
import { resolveDraftStyleSample } from "../../../WriteSettings/muse2Alpha";
import StickyNoteAnchor from "../../../StickyTour/StickyNoteAnchor";
import {
  createDraftGrammarSuppressionRelease,
  setDraftGrammarChecksSuppressed,
} from "./draftGrammarSuppression";
import { buildLocalDraftPreflightResult, requireUsableDraftEstimate } from "./draftPreflightContract";
import DraftProseAccessGate from "./DraftProseAccessGate";
import { createDraftPauseTelemetry } from "./draftPauseTelemetry";

const MainContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  border-top: 1px solid ${themeVariables.divider};
  border-bottom-left-radius: 20px;
  border-bottom-right-radius: 20px;
  position: relative;
  background-color: ${themeVariables.menuColorFullscreen};
  z-index: 2;

  svg {
    path {
      fill: ${themeVariables.ctaTextColor};
    }
  }

  .baseButton {
    border-radius: 8px !important;
    padding: 8px !important;
    cursor: pointer;
  }

  .pauseButton {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;

    .labels {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-start;
      .label {
        font-size: 14.8px;
        font-weight: 500;
      }

      .sublabel {
        font-size: 10px;
        font-weight: 600;
        min-width: 130px !important;
        text-align: left;
      }
    }
    svg {
      height: 24px;
      width: 24px;
      rect {
        fill: var(--cta-text-color);
      }
    }
  }

  .cancelButton {
    display: flex;
    align-items: center;
    justify-content: center;
    svg {
      rect {
        fill: var(--cta-text-color);
      }
    }
  }
`;

const InnerContainer = styled.div`
  width: calc(100% - 40px);
  padding: 20px;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  .title-large {
    font-size: 18px;
    font-weight: 500;
  }
`;

const ButtonContainer = styled.div`
  //   container-type: inline-size;
  //   width: 100%;
  //   display: flex;
  //   flex-wrap: wrap;
  //   gap: 10px;
  width: 100%;
  .scene-warnings {
    display: flex;
    align-items: center;
    gap: 8px;
    border: 1px solid var(--divider);
  }
  .warning {
    height: 24px;
    width: 24px;
    path {
      fill: var(--warning-color);
    }
  }
`;
const modeMap = {
  planning: "Thinking...",
  writing: "Writing...",
};


const GeneratePanel = forwardRef(
  (
    {
      documentId,
      projectId,
      setDocumentScenesString,
      editor,
      editorRef,
      editorEndRef,
      draftStateMachine,
      setIsStreamingScenes,
      proseMode,
      missingScenesAndNotConnected,
      scenePersistence,
      comparisonEditorsRef,
      comparisonInvocationIdRef,
      streamBSceneIndexRef,
      setComparisonMode,
      setComparisonVisible,
      addDraftPlaceholderCard,
      appendDraftPlaceholderContent,
      linkDraftPlaceholderToCardId,
      finalizeDraftPlaceholder,
      removeDraftPlaceholderCard,
      onEditorLayoutChange,
    },
    ref,
  ) => {
    const features = useSudowriteStore((state) => state.getFeatureFlags)();
    const { hasAccessToPOVAndTenseBeta, hasAccessToStoryEngineProseGenerate, hasAccessToStyleGuide } = features || {};
    const sprint = useNovelNovemberSprint();
    const freeSprint = sprint.draftAvailable && proseMode?.scenesEngine === "ballad-1.1-draft" && !proseMode?.benchmarkMode;
    const draftProseAccessDisabled = hasAccessToStoryEngineProseGenerate === false;

    const { setToast } = useToast();
    const sceneToWrite = useStoryBibleStore((state) => state.sceneToWrite);
    const setScenes = useStoryBibleStore((state) => state.setScenes);
    const lastInvocation = useStoryBibleStore((state) => state.lastInvocation);
    const setLastInvocation = useStoryBibleStore((state) => state.setLastInvocation);
    const outOfQuota = useStoryBibleStore((state) => state.outOfQuota);
    const requesting = useStoryBibleStore((state) => state.requesting);
    const setRequesting = useStoryBibleStore((state) => state.setRequesting);
    const abortStream = useStoryBibleStore((state) => state.abortStream);
    const abortController = useStoryBibleStore((state) => state.abortController);
    const scenes = useStoryBibleStore((state) => state.scenes);
    const sceneId = useStoryBibleStore((state) => state.sceneId);
    const documentScenes = useStoryBibleStore((state) => state.documentScenes?.[documentId]);
    const getDocumentScenes = useStoryBibleStore((state) => state.getDocumentScenes);
    const setPreflightResponse = useStoryBibleStore((state) => state.setPreflightResponse);
    const preflightResponse = useStoryBibleStore((state) => state.preflightResponse);
    const getExtraInstructionsWordCount = useStoryBibleStore((state) => state.getExtraInstructionsWordCount);
    const getDocumentSceneCount = useStoryBibleStore((state) => state.getDocumentSceneCount);
    const PROSE_MODES = useStoryBibleStore((state) => state.PROSE_MODES);
    const streamRequestRef = useStoryBibleStore((state) => state.streamRequestRef);
    const recentInvocations = useStoryBibleStore((state) => state.recentInvocations);
    const userData = useSudowriteStore((state) => state.userData);
    const subscription = useSudowriteStore((state) => state.subscription);
    const generationOutOfQuota = outOfQuota && !freeSprint;
    const supportFormUrl = useMemo(() => getAirTableSupportUrl(subscription), [subscription]);
    const streamTimedOut = useStoryBibleStore((state) => state.streamTimedOut);
    const resetScrollCount = useProjectStore((state) => state.resetScrollCount);
    const automaticScroll = useProjectStore((state) => state.automaticScroll);
    const setUserScrolled = useProjectStore((state) => state.setUserScrolled);
    const outline = useStructuredOutlineStore((state) => state.outline);
    const isMultiplePOV = useProjectStore((state) => state.isMultiplePOV);
    const projectPOVTense = useProjectStore((state) => state.povTense);
    const currentDocumentPOVTense = useProjectStore((state) => state.currentDocumentPOVTense);
    const streamRequest = streamRequestRef.current;
    const [proseWords, setProseWords] = useState(0);
    const highQuality = useSudowriteStore((state) => state.editorOptions?.draftHighQuality ?? true);
    const setHighQuality = useSudowriteStore((state) => state.setDraftHighQuality);
    const [currentProseMode, setCurrentProseMode] = useState(modeMap.planning);
    const currentProseModeRef = useRef(modeMap.planning);
    const draftPauseTelemetryRef = useRef(null);
    const comparisonModeRef = useRef(false);
    const streamBCommandIndex = useRef(0);
    const markdownStreamerRef = useRef(null);
    const draftInsertionStateRef = useRef({ prepared: false });
    const placeholderCardRef = useRef(null);
    const preflightRequestSnapshotRef = useRef(null);
    const [povTense, setPOVTense] = useState({
      pov: { value: null, characters: [], userChanged: false },
      tense: { value: null, userChanged: false },
    });
    // Fallback chain per field: local state → document-level store → project-level store → defaults
    // POV and tense are resolved independently so a change to one doesn't lose the other.
    const effectivePOVTense = {
      pov: povTense.pov.value != null ? povTense.pov :
        currentDocumentPOVTense?.pov?.value != null ? currentDocumentPOVTense.pov :
        projectPOVTense?.pov?.value != null ? projectPOVTense.pov :
        DEFAULT_POV_TENSE_OBJECT.pov,
      tense: povTense.tense.value != null ? povTense.tense :
        currentDocumentPOVTense?.tense?.value != null ? currentDocumentPOVTense.tense :
        projectPOVTense?.tense?.value != null ? projectPOVTense.tense :
        DEFAULT_POV_TENSE_OBJECT.tense,
    };
    const selectedCharacters = useCharacterNames(effectivePOVTense.pov.characters);
    const povCharacterNames = useCharacterNames(effectivePOVTense.pov.characters);
    const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);
    const sceneSummary = sceneToWrite?.summary;
    const scene = scenes?.[sceneId];

    const syncPersistDraftPreflightSnapshot = async (preflightPayload) => {
      try {
        await scenePersistence.reconcileMetadata(() => persistDraftPreflightSnapshot(documentId, preflightPayload));
      } catch (e) {
        console.error("persistDraftPreflightSnapshot:", e);
      }
    };

    const syncClearDraftPreflightSnapshot = async () => {
      try {
        await scenePersistence.reconcileMetadata(() => clearDraftPreflightSnapshot(documentId));
      } catch (e) {
        console.error("clearDraftPreflightSnapshot:", e);
      }
    };

    const rewriteScenes = async (rewritePrompt) => {
      setIsStreamingScenes(true);
      setRequesting(true);
      draftStateMachine.events.requestRewriteScenes();
      const url = "/api/ai/storyEngine/write/rewrite-scenes";
      AppEventPublisher.storyBible.draft.rewriteScenes(proseMode.scenesEngine);

      // Gate the function on persistence completing. Returning flips
      // PreflightAnalysis's local `isRewriting` back to false (hiding
      // "Rewriting…") and re-enables Generate Draft, so we must not
      // return until the rewritten scenes are actually saved.
      //
      // Two failure modes if we don't gate:
      // 1) scenePersistence.flush() finds no pending PUT because
      //    the React effect that schedules it (TiptapListField's
      //    updateValueString effect → onChange → scenePersistence.schedule)
      //    hasn't run yet when onEnd fires. The save is then issued
      //    1s late or, if the user re-runs preflight first, never.
      // 2) syncClearDraftPreflightSnapshot is GET + mutate + PUT. When
      //    fired in parallel with the save, its GET reads pre-rewrite
      //    scenes and its PUT overwrites the rewrite with the original
      //    content — visible to the user as scenes briefly appearing
      //    rewritten, then snapping back.
      let resolveStreamEnd;
      const streamEndPromise = new Promise((resolve) => {
        resolveStreamEnd = resolve;
      });
      let didComplete = false;
      let wasAborted = false;
      const signal = abortController.current.signal;
      const onAbort = () => {
        wasAborted = true;
        resolveStreamEnd();
      };
      signal.addEventListener("abort", onAbort);

      try {
        streamRequest(
          url,
          {
            sceneSummary,
            sceneId,
            rewritePrompt,
            hasAccessToPOVAndTenseBeta,
            povTense: hasAccessToPOVAndTenseBeta ? { ...effectivePOVTense, pov: { ...effectivePOVTense.pov, characters: povCharacterNames } } : null,
            scenesEngine: proseMode.scenesEngine,
            ...(freeSprint && { novelNovemberSprint: true }),
            ...(proseMode.benchmarkMode && { benchmarkMode: true }),
            context_project_id: projectId,
          },
          (text) => {
            if (text) {
              setDocumentScenesString(text);
            }
          },
          signal,
          `rewrite-scenes-${sceneId}`,
          null,
          () => {
            didComplete = true;
            resolveStreamEnd();
          },
          undefined,
          undefined,
          () => {
            resolveStreamEnd();
          }
        );
        await streamEndPromise;
      } finally {
        signal.removeEventListener("abort", onAbort);
      }

      if (didComplete && !wasAborted) {
        // Yield so React commits the final setDocumentScenesString and
        // TiptapListField's updateValueString effect runs onChange →
        // scenePersistence.schedule(newValue). That's what gives flush()
        // a pending PUT to fire.
        await new Promise((resolve) => setTimeout(resolve, 0));

        try {
          await scenePersistence.flush();
        } catch (e) {
          console.error("Failed to persist rewritten scenes:", e);
          setIsStreamingScenes(false);
          setRequesting(false);
          return false; // Signal failure — do NOT clear warning or emit completion
        }

        // Sequential after the save so its GET reads the just-saved
        // rewrite and its PUT doesn't stomp it.
        await syncClearDraftPreflightSnapshot();

        setPreflightResponse(null);
        draftStateMachine.events.rewriteScenesCompleted();
      }

      setIsStreamingScenes(false);
      setRequesting(false);
      return didComplete && !wasAborted;
    };

    const prosePreflight = async () => {
      if (draftProseAccessDisabled) return false;

      try {
        draftStateMachine.events.startPreflight();
        setRequesting(true);

        // Include saving in the preflight lifecycle so failures reach error review.
        await scenePersistence.flush();
        AppEventPublisher.storyBible.draft.preflightProse(proseMode.engine, scene.isContinue);

        // Establish the server-signed billing contract before returning any
        // generation-permitting preflight result, including local suggestions.
        const freshDocumentScenes = getDocumentScenes(documentId);
        const commands = getTextFromTiptapListField(freshDocumentScenes?.value?.scenes);
        const text = editor.getText();
        // Store the fingerprinted request data so generateProse uses the same
        // values the server signed, even if the user edits scenes in between.
        preflightRequestSnapshotRef.current = { commands, text };
        const estimatorRes = requireUsableDraftEstimate(
          await Auth.fetchWithIdToken("/api/ai/storyEngine/write/estimateChapter", {
            commands,
            sceneSummary,
            startCommandIndex: scene?.restartFromIndex || 0,
            text,
            engine: proseMode.engine,
            maxTokens: proseMode.maxTokens,
            temperature: scene?.creativityLevel,
            isContinue: scene?.isContinue,
            version: proseMode.version,
            userStyleExamples: resolveDraftStyleSample(userData?.data, !!proseMode?.muse2Alpha),
            scenesEngine: proseMode.scenesEngine,
            ...(freeSprint && { novelNovemberSprint: true }),
            ...(proseMode.benchmarkMode && { benchmarkMode: true }),
            modelLabel: proseMode.label,
            documentId: documentId,
            context_project_id: projectId,
          })
        );

        const localCheckResult = buildLocalDraftPreflightResult({
          extraInstructionsWordCount: getExtraInstructionsWordCount(documentId),
          documentSceneCount: getDocumentSceneCount(documentId),
          estimates: estimatorRes,
        });
        if (localCheckResult) {
          AppEventPublisher.storyBible.draft.preflightLocalCheck(localCheckResult.reason);
          setPreflightResponse(localCheckResult.response);
          AppEventPublisher.storyBible.draft.preflightCheckResult("Suggestion");
          await syncPersistDraftPreflightSnapshot(localCheckResult.response);
          setRequesting(false);
          return;
        }

        const analysisRes = await Auth.fetchWithIdToken("/api/ai/storyEngine/write/analyze-scenes", {
          documentId: documentId,
          context_project_id: projectId,
          scenesEngine: proseMode.scenesEngine,
          ...(estimatorRes.novelNovemberSprint && { novelNovemberSprint: true }),
          ...(proseMode.benchmarkMode && { benchmarkMode: true }),
          hasAccessToPOVAndTenseBeta,
          povTense: hasAccessToPOVAndTenseBeta ? { ...effectivePOVTense, pov: { ...effectivePOVTense.pov, characters: povCharacterNames } } : null,
        });
        // Combine analysis and estimation results
        const combinedResult = {
          ...analysisRes,
          estimates: { ...estimatorRes },
        };
        AppEventPublisher.storyBible.draft.preflightCheckResult(combinedResult.ResponseType);
        setPreflightResponse(combinedResult);
        await syncPersistDraftPreflightSnapshot(combinedResult);
      } catch (error) {
        console.error("Error in prosePreflight:", error);
        // Block generation when estimation fails — user must retry
        const fallbackResponse = {
          ResponseType: "Error",
          Diagnosis: getSprintErrorMessage(error) || "There was an error estimating credits for this chapter. Please retry — if the problem persists, try splitting your chapter into fewer scenes.",
          QuickFixCTA: "Retry",
          RewritePrompt: "",
        };

        if (error?.recursionRisk) {
          fallbackResponse.estimates = {
            recursionRisk: error.recursionRisk,
          };
        }

        setPreflightResponse(fallbackResponse);
        AppEventPublisher.storyBible.draft.preflightCheckResult("Error");
        await syncPersistDraftPreflightSnapshot(fallbackResponse);
      } finally {
        setRequesting(false);
        draftStateMachine.events.preflightCompleted();
      }
    };

    const sendInvocationEventsForProseGenerate = (sceneId) => {
      const commandInvocationId = recentInvocations.commands[sceneId]; // gets the most recent command invocation for this prose generate

      if (commandInvocationId) {
        // record an event that tracks when a prose generation occurs for an "untouched" command invocation
        sendInvocationEvent(commandInvocationId, "prose-generated-from-scenes");
      }
    };

    const insertDraftContent = (content) => insertDraftStreamContent(editor, content, draftInsertionStateRef.current);

    const updateEditor = (rawChunkText) => {
      const chunkText = convertQuotes(rawChunkText, editor);
      if (chunkText) {
        editor.commands.focus("end");
        const streamer = markdownStreamerRef.current;
        const lines = splitStreamChunkLines(chunkText, editor);

        if (chunkText.indexOf("\n") === -1 && lines.length === 1) {
          // no newline present, so we just insert
          const html = streamer ? streamer.write(lines[0]) : lines[0];
          if (html) {
            insertDraftContent(html);
          }

          automaticScroll(editorEndRef);
        } else {
          for (let i = 0; i < lines.length; i++) {
            if (lines[i].length > 0) {
              const html = streamer ? streamer.write(lines[i]) : lines[i];
              if (html) {
                insertDraftContent(html);
              }
            }

            if (i < lines.length - 1) {
              // Flush pending markdown before paragraph breaks
              if (streamer) {
                const pendingHtml = streamer.flush();
                if (pendingHtml) {
                  insertDraftContent(pendingHtml);
                }
              }
              // Plain paragraph boundary — no spacer empty paragraph; visual
              // separation comes from the Paragraph Spacing setting.
              insertDraftContent([
                {
                  type: "paragraph",
                  content: [],
                },
              ]);
            }
          }

          // Resize iframe after multiline content insertion grows the document
          onEditorLayoutChange?.();
        }
      }
    };

    const generateProse = async ({ benchmarkRetry = false } = {}) => {
      if (requesting || generationOutOfQuota || draftProseAccessDisabled) return false;
      // Recover tabs whose controller was aborted by an older benchmark exit.
      if (abortController.current.signal.aborted) abortStream();
      // Bind this generation to the controller that exists now. `abortStream`
      // replaces the shared controller when it cancels, so a cancel that lands
      // during the awaits below (closing a Write Again replay while it is
      // still loading) must still abort this request, not a fresh controller.
      const controller = abortController.current;
      setCurrentProseMode(modeMap.planning);
      currentProseModeRef.current = modeMap.planning;
      const pauseTelemetry = createDraftPauseTelemetry({
        publish: AppEventPublisher.storyBible.draft.pauseProseGeneration,
        countWords,
      });
      draftPauseTelemetryRef.current = pauseTelemetry;
      clearPendingStreamChunkBoundary(editor);
      draftInsertionStateRef.current = { prepared: false };
      setPreflightResponse(null);
      editorRef && automaticScroll(editorRef);
      draftStateMachine.events.startGeneratingProse();
      // Use the preflight snapshot (commands + text that were fingerprinted by the
      // server) when available, so the generation request matches the signed contract.
      // Falls back to fresh store data only if no preflight was run (shouldn't happen
      // in normal flow, but guards against edge cases).
      const snapshot = preflightRequestSnapshotRef.current;
      const commands = snapshot?.commands ?? getTextFromTiptapListField(getDocumentScenes(documentId)?.value?.scenes);
      if (!commands.trim().length === 0) {
        setToast("Please write Scenes first");
        return false;
      }

      // Flush any pending debounced scene saves so the server has the latest
      // scenes before we clear the preflight snapshot (which does GET + PUT).
      try {
        await scenePersistence.flush();
      } catch (e) {
        console.error("generateProse: failed to flush pending scene save:", e);
        draftStateMachine.events.cancelProseGeneration();
        return false;
      }
      await syncClearDraftPreflightSnapshot();
      comparisonModeRef.current = false;
      if (proseMode.benchmarkMode) {
        setComparisonMode(false);
        setComparisonVisible(false);
      }
      const baseCommandIndex = scene?.lastCommandIndex || 0;
      streamBCommandIndex.current = baseCommandIndex;

      const ctx = {
        comparisonModeRef,
        comparisonEditorsRef,
        comparisonInvocationIdRef,
        streamBCommandIndex,
        streamBSceneIndexRef,
        comparisonVisibleFired: { current: false },
        localInvocationId: { current: null },
        baseCommandIndex,
        setComparisonMode,
        setComparisonVisible,
        setScenes,
        setProseWords,
        setCurrentProseMode,
        setRequesting,
        currentProseModeRef,
        modeMap,
        draftStateMachine,
        documentId,
        sceneId,
        editor,
        streamerA: new MarkdownStreamer(),
        streamerB: new MarkdownStreamer(),
      };

      // Muse 2 Alpha announces its comparison with a benchmarkSessionId.
      // Route that stream to the takeover instead of the manuscript editor.
      const benchmarkCtx = createBenchmarkInterceptContext({
        surface: "draft",
        onWriteAgain: () => {
          generateProse({ benchmarkRetry: true });
          // Closing while the replay is still loading aborts it too.
          return abortStream;
        },
        // The shared cancel action also replaces the controller for the next Draft.
        onAbort: abortStream,
      });
      if (benchmarkRetry) benchmarkCtx.expectSession = true;

      sendInvocationEventsForProseGenerate(sceneId);
      AppEventPublisher.storyBible.draft.generateProse(proseMode.scenesEngine, scene.isContinue);
      setDraftGrammarChecksSuppressed(editor, true);
      const releaseDraftGrammarSuppression = createDraftGrammarSuppressionRelease({
        editor,
        comparisonEditorsRef,
      });
      editor.setOptions({ editable: false });
      markdownStreamerRef.current = new MarkdownStreamer();
      const scenesEngine = proseMode.scenesEngine;
      const url = "/api/ai/storyEngine/write/chapterGenerator";

      // SW-9395: Do NOT create the placeholder history card until generation
      // completes. Accumulate text locally during streaming, then create and
      // finalize the card in onEnd/onTimeout/onError.
      let accumulatedNormalText = '';
      let accumulatedComparisonText = '';
      let receivedCardId = null;
      let failureToastShown = false;
      let receivedStreamError = false;
      let terminalHandled = false;
      let normalSceneStarts = 0;
      const startingProgress = {
        lastCommandIndex: scene?.lastCommandIndex,
        restartFromIndex: scene?.restartFromIndex,
        generationFinished: scene?.generationFinished,
        isContinue: scene?.isContinue,
      };
      const containsProse = (text) => text.replace(/^\s*#\s*Scene\s+\d+\s*$/gim, "").trim().length > 0;
      const hasDraftProse = () => [accumulatedNormalText, accumulatedComparisonText].some(containsProse);
      const leadingText = { normal: "", A: "", B: "" };
      const textReadyForEditor = (stream, text) => {
        // Keep initial headings out of the manuscript until prose arrives.
        // An empty attempt can then retry without leaving a duplicate heading.
        if (leadingText[stream] === null) return text;
        leadingText[stream] += text;
        if (!containsProse(leadingText[stream])) return "";
        const ready = leadingText[stream];
        leadingText[stream] = null;
        return ready;
      };
      const notifyEmptyDraft = () => {
        // Intentional Cancel/Pause aborts this request's controller. An
        // AbortError from the network alone does not establish user intent.
        if (controller.signal.aborted || hasDraftProse() || receivedStreamError) return false;
        if (!failureToastShown) {
          setToast("Draft stopped before any prose was generated. Please try again.");
          failureToastShown = true;
        }
        return true;
      };
      const settleEmptyDraft = () => {
        if (hasDraftProse()) return false;
        preflightRequestSnapshotRef.current = null;
        setPreflightResponse(null);
        setRequesting(false);
        setProseWords(0);
        editor.setOptions({ editable: true });
        releaseDraftGrammarSuppression();
        markdownStreamerRef.current = null;
        placeholderCardRef.current = null;
        comparisonModeRef.current = false;
        setComparisonMode(false);
        setComparisonVisible(false);
        // Headings may have advanced the scene cursor before prose arrived.
        // An empty attempt must retain its original retry position.
        setScenes((draft) => {
          if (!draft[sceneId]) return;
          for (const [key, value] of Object.entries(startingProgress)) {
            if (value === undefined) delete draft[sceneId][key];
            else draft[sceneId][key] = value;
          }
        });
        draftStateMachine.events.cancelProseGeneration();
        return true;
      };
      const preparePartialDraftRetry = () => {
        if (comparisonModeRef.current || !containsProse(accumulatedNormalText)) return;
        preflightRequestSnapshotRef.current = null;
        setPreflightResponse(null);
        setRequesting(false);
        setScenes((draft) => {
          draft[sceneId].isContinue = true;
          // Scene-start signals advance the shared cursor before completion.
          // Resume the latest started scene relative to this request's selection.
          draft[sceneId].restartFromIndex = (startingProgress.restartFromIndex ?? 0) + Math.max(0, normalSceneStarts - 1);
          draft[sceneId].lastCommandIndex = draft[sceneId].restartFromIndex;
          draft[sceneId].generationFinished = false;
        });
      };

      await streamRequest(
        url,
        {
          commands,
          sceneSummary,
          startCommandIndex: scene.restartFromIndex || 0,
          text: snapshot?.text ?? editor.getText(),
          isErrorRecovery: streamTimedOut,
          ...(freeSprint && { novelNovemberSprint: true }),
          engine: proseMode.engine,
          maxTokens: proseMode.maxTokens,
          temperature: scene.creativityLevel,
          isContinue: scene.isContinue,
          version: proseMode.version,
          userStyleExamples: resolveDraftStyleSample(userData?.data, !!proseMode?.muse2Alpha),
          scenesEngine,
          ...(hasAccessToStyleGuide && { highQuality: highQuality && !proseMode.benchmarkMode }),
          modelLabel: proseMode.label,
          ...(proseMode.benchmarkMode && { benchmarkMode: true }),
          estimateData: preflightResponse ? preflightResponse.estimates : null,
          hasAccessToPOVAndTenseBeta,
          ...(hasAccessToPOVAndTenseBeta && {
            ...(!isAutoPOV(effectivePOVTense.pov.value) && { pov: effectivePOVTense.pov.value }),
            ...(effectivePOVTense.pov.value !== POVS.THIRD_PERSON_OMNISCIENT && { povCharacters: selectedCharacters }),
            ...(!isAutoTense(effectivePOVTense.tense.value) && { tense: effectivePOVTense.tense.value }),
          }),
          ...(benchmarkRetry && { benchmarkRetry: true }),
        },
        (chunkText) => {
          // benchmark sessions render in the takeover, never the editor
          if (benchmarkCtx.active) return;
          // if we're in comparison mode, don't update the main editor
          if (comparisonModeRef.current) return;
          const ready = textReadyForEditor("normal", chunkText);
          if (ready) {
            updateEditor(ready);
            pauseTelemetry.deliver(ready);
          }
          // Accumulate text for the history card (created at end of generation).
          accumulatedNormalText += chunkText;
        },
        controller.signal,
        `sceneProse-${sceneId}`,
        (chunk) => {
          pauseTelemetry.receive(chunk);
          if (maybeInterceptBenchmarkChunk(chunk, benchmarkCtx)) {
            draftStateMachine.events.proseReceived();
            return;
          }
          if (chunk.comparisonMode && !proseMode.benchmarkMode) {
            initializeComparisonMode(ctx);
            return;
          }
          // Invocation metadata can arrive before the benchmark session frame.
          // Never persist it as a pending legacy comparison for this mode.
          if (chunk.initialInvocationId && !proseMode.benchmarkMode) {
            persistComparisonInvocationId(chunk, ctx);
          }
          if (chunk.cardId) {
            receivedCardId = chunk.cardId;
          }
          // streamRequest already displays the server's specific error message.
          if (chunk.error) receivedStreamError = true;
          if (chunk.text && chunk.stream && comparisonModeRef.current) {
            const idx = chunk.stream === "B" ? 1 : 0;
            if (idx === 0) {
              accumulatedNormalText += chunk.text;
            } else {
              accumulatedComparisonText += chunk.text;
            }
          }
          if (comparisonModeRef.current) {
            const displayChunk = chunk.text && chunk.stream
              ? { ...chunk, text: textReadyForEditor(chunk.stream, chunk.text) }
              : chunk;
            handleComparisonChunk(displayChunk, ctx);
            return;
          }
          if (chunk.incrementScene !== undefined) normalSceneStarts++;
          handleNormalChunk(chunk, ctx);
        },
        () => {
          // onEnd
          if (terminalHandled) return;
          terminalHandled = true;
          preflightRequestSnapshotRef.current = null;
          if (benchmarkCtx.active) {
            // Benchmark session: reveal the samples in the takeover. The
            // server owns history for benchmark runs, so no local history
            // card is created.
            benchmarkStreamEnded(benchmarkCtx);
            editor.setOptions({ editable: true });
            releaseDraftGrammarSuppression();
            setProseWords(0);
            setRequesting(false);
            draftStateMachine.events.proseCompleted();
            return;
          }
          if (benchmarkCtx.expectSession) {
            // A Write Again replay the server chose not to intercept streamed
            // into the editor as a normal generation; close the takeover.
            benchmarkStreamEnded(benchmarkCtx);
          }
          notifyEmptyDraft();
          if (settleEmptyDraft()) {
            // The onboarding tour must also leave its waiting step after an empty run.
            sendFlowSignal(TourSignal.DRAFT_PROSE_GENERATE_GO_COMPLETED);
            return;
          }
          if (comparisonModeRef.current) {
            handleComparisonEnd(ctx);
            setProseWords(0);
            sendFlowSignal(TourSignal.DRAFT_PROSE_GENERATE_GO_COMPLETED);
            // Create completed history card now that generation is done
            if (accumulatedNormalText || accumulatedComparisonText) {
              const completedCard = addDraftPlaceholderCard?.({ prompt: commands, comparisonMode: true, modelLabel: proseMode?.label });
              if (completedCard) {
                completedCard.data.content = [accumulatedNormalText, accumulatedComparisonText];
                if (receivedCardId) {
                  linkDraftPlaceholderToCardId?.(completedCard, receivedCardId);
                }
                placeholderCardRef.current = completedCard;
                finalizeDraftPlaceholder?.(completedCard);
                placeholderCardRef.current = null;
              }
            }
            releaseDraftGrammarSuppression();
            return;
          }

          // Flush any remaining markdown tokens
          if (markdownStreamerRef.current) {
            const remainingHtml = markdownStreamerRef.current.flush();
            if (remainingHtml) {
              insertDraftContent(remainingHtml);
            }
          }
          // Final resize to ensure iframe height matches completed content
          onEditorLayoutChange?.();
          editor.setOptions({ editable: true });
          releaseDraftGrammarSuppression();
          setProseWords(0);
          setRequesting(false);
          sendFlowSignal(TourSignal.DRAFT_PROSE_GENERATE_GO_COMPLETED);
          draftStateMachine.events.proseCompleted();
          // Detect if it's a My Voice model
          if (proseMode?.key?.startsWith("CUSTOM_")) {
            const { beats } = parseCommands(scene?.commands);
            setScenes((draft) => {
              const maxCommandIndex = beats.length - 1;
              // Increment the current scene index unless it's the last one, so that My Voice users can easily run the next stride
              if (draft[sceneId].lastCommandIndex < maxCommandIndex) {
                draft[sceneId].lastCommandIndex = (draft[sceneId].lastCommandIndex || 0) + 1;
                draft[sceneId].restartFromIndex = draft[sceneId].lastCommandIndex;
              }
              draft[sceneId].generationFinished = true;
            });
          } else {
            setScenes((draft) => {
              delete draft[sceneId].lastCommandIndex;
              delete draft[sceneId].isContinue;
              draft[sceneId].generationFinished = true;
              draft[sceneId].restartFromIndex = 0;
            });
          }
          // Create completed history card now that generation is done
          if (accumulatedNormalText) {
            const completedCard = addDraftPlaceholderCard?.({ prompt: commands, comparisonMode: false, modelLabel: proseMode?.label });
            if (completedCard) {
              completedCard.data.content = [accumulatedNormalText];
              if (receivedCardId) {
                linkDraftPlaceholderToCardId?.(completedCard, receivedCardId);
              }
              placeholderCardRef.current = completedCard;
              finalizeDraftPlaceholder?.(completedCard);
              placeholderCardRef.current = null;
            }
          }
        },
        () => {
          // onTimeout
          if (terminalHandled) return;
          terminalHandled = true;
          if (benchmarkCtx.active) {
            benchmarkStreamFailed(benchmarkCtx);
            editor.setOptions({ editable: true });
            releaseDraftGrammarSuppression();
            draftStateMachine.events.proseCompleted();
            return;
          }
          notifyEmptyDraft();
          if (settleEmptyDraft()) return;
          if (markdownStreamerRef.current) {
            const remainingHtml = markdownStreamerRef.current.flush();
            if (remainingHtml) {
              insertDraftContent(remainingHtml);
            }
          }
          editor.setOptions({ editable: true });
          releaseDraftGrammarSuppression();
          preparePartialDraftRetry();
          draftStateMachine.events.proseCompleted();
          // Create completed history card with whatever was accumulated before timeout
          if (accumulatedNormalText || accumulatedComparisonText) {
            const isComparison = !!accumulatedComparisonText;
            const completedCard = addDraftPlaceholderCard?.({ prompt: commands, comparisonMode: isComparison, modelLabel: proseMode?.label });
            if (completedCard) {
              completedCard.data.content = isComparison ? [accumulatedNormalText, accumulatedComparisonText] : [accumulatedNormalText];
              if (receivedCardId) {
                linkDraftPlaceholderToCardId?.(completedCard, receivedCardId);
              }
              placeholderCardRef.current = completedCard;
              finalizeDraftPlaceholder?.(completedCard);
              placeholderCardRef.current = null;
            }
          }
        },
        () => {
          // onStart
          setRequesting(true);
          setUserScrolled(false);
          resetScrollCount();
          setPreflightResponse(null);
        },
        (err) => {
          // onError
          if (terminalHandled) return false;
          terminalHandled = true;
          preflightRequestSnapshotRef.current = null;
          if (benchmarkCtx.active) {
            // Benchmark generation failed: the takeover closes itself with a
            // toast, so suppress streamRequest's default error toast.
            benchmarkStreamFailed(benchmarkCtx, err);
            setPreflightResponse(null);
            setRequesting(false);
            editor.setOptions({ editable: true });
            releaseDraftGrammarSuppression();
            draftStateMachine.events.proseCompleted();
            return true;
          }
          // streamRequest already toasts ordinary errors, including rejected
          // estimates and validation errors. Only fill its silent abort path.
          const isModelOutage = isProbableModelOutageError(err);
          const notifiedEmptyDraft = !!err?.message?.includes("abort") && !isModelOutage && notifyEmptyDraft();
          if (isModelOutage) {
            setToast(
              <ModelOutageMessage
                supportUrl={supportFormUrl}
                className="text-app-danger-color"
                linkClassName="hover:brightness-[1.50] transition-all duration-200 ease-in-out underline text-app-danger-color pl-0"
              />,
              {
                dismissible: true,
                durationMs: 20000,
              }
            );
          }
          // A later end/timeout callback must not replace the specific error.
          receivedStreamError = true;
          // All empty attempts stop here, including outages with only headings.
          // Error presentation must not route them through partial-prose saving.
          if (settleEmptyDraft()) return notifiedEmptyDraft || isModelOutage;
          if (handleComparisonAbortError(err, ctx)) {
            // Comparison mode aborts preserve the picker UI; create card with accumulated content.
            if (accumulatedNormalText || accumulatedComparisonText) {
              const isComparison = !!accumulatedComparisonText;
              const completedCard = addDraftPlaceholderCard?.({ prompt: commands, comparisonMode: isComparison, modelLabel: proseMode?.label });
              if (completedCard) {
                completedCard.data.content = isComparison ? [accumulatedNormalText, accumulatedComparisonText] : [accumulatedNormalText];
                if (receivedCardId) {
                  linkDraftPlaceholderToCardId?.(completedCard, receivedCardId);
                }
                finalizeDraftPlaceholder?.(completedCard);
              }
            }
            placeholderCardRef.current = null;
            releaseDraftGrammarSuppression();
            return notifiedEmptyDraft || isModelOutage;
          }

          if (markdownStreamerRef.current) {
            const remainingHtml = markdownStreamerRef.current.flush();
            if (remainingHtml) {
              insertDraftContent(remainingHtml);
            }
          }
          setPreflightResponse(null);
          setRequesting(false);
          editor.setOptions({ editable: true });
          releaseDraftGrammarSuppression();
          preparePartialDraftRetry();
          comparisonModeRef.current = false;
          setComparisonMode(false);
          setComparisonVisible(false);
          if (accumulatedNormalText || accumulatedComparisonText) {
            draftStateMachine.events.proseCompleted();
          } else {
            // A rejected/expired estimate fails before the stream starts.
            // Use cancelProseGeneration to preserve POST_GENERATED_IDLE state
            // from a prior successful run while allowing rerun of preflight.
            draftStateMachine.events.cancelProseGeneration();
          }

          // If we accumulated any content, create and finalize the card.
          // Otherwise do nothing (no card was ever created).
          if (accumulatedNormalText || accumulatedComparisonText) {
            const isComparison = !!accumulatedComparisonText;
            const completedCard = addDraftPlaceholderCard?.({ prompt: commands, comparisonMode: isComparison, modelLabel: proseMode?.label });
            if (completedCard) {
              completedCard.data.content = isComparison ? [accumulatedNormalText, accumulatedComparisonText] : [accumulatedNormalText];
              if (receivedCardId) {
                linkDraftPlaceholderToCardId?.(completedCard, receivedCardId);
              }
              finalizeDraftPlaceholder?.(completedCard);
            }
          }
          placeholderCardRef.current = null;
          return notifiedEmptyDraft || isModelOutage;
        }
      );

      return true;
    };
    const showPreflight =
      draftStateMachine.value === DraftStates.PREFLIGHT_RUNNING || draftStateMachine.value === DraftStates.PREFLIGHT_REVIEW || draftStateMachine.value === DraftStates.REWRITING_SCENES;

    return (
      <MainContainer ref={ref}>
        <InnerContainer>
          <StickyNoteAnchor anchorId={AnchorIds.DRAFT_GENERATE_PANEL} />
          <DraftProseAccessGate blocked={draftProseAccessDisabled}>
          <HeaderContainer style={{ containerType: "inline-size", contain: "layout" }}>
            <div
              className={`flex ${hasAccessToPOVAndTenseBeta ? `flex-row justify-between` : "flex-row justify-between"} items-start w-full gap-4 flex-wrap @3xl:flex-nowrap @lg:flex-col`}
              style={{ containerType: "inline-size", contain: "layout" }}
            >
              {hasAccessToPOVAndTenseBeta && !showPreflight ? null : (
                <div className={`flex justify-start items-start flex-col gap-3  @xl:flex-row self-start`}>
                  <div className={`title-large ${showPreflight ? "mb-1" : "mb-0"}`}>
                    {showPreflight ? (draftStateMachine.value === DraftStates.PREFLIGHT_REVIEW ? "Review Plan" : "") : "Prose"}
                  </div>
                </div>
              )}
              <XStateConditionalRenderer
                exclusiveStates={[DraftStates.PREFLIGHT_RUNNING, DraftStates.PREFLIGHT_REVIEW, DraftStates.REWRITING_SCENES]}
                currentState={draftStateMachine.value}
              >
                <ButtonContainer>
                  <div className="w-full flex flex-wrap items-end gap-8">
                    <div className="w-auto">
                      {hasAccessToPOVAndTenseBeta && !showPreflight ? (
                        <POVTenseButtons
                          draftStateMachine={draftStateMachine}
                          povTense={effectivePOVTense}
                          setPOVTense={setPOVTense}
                          outline={outline}
                          className={""}
                          projectId={projectId}
                          disableContainerQuery
                          allowWrap
                          disabled={draftStateMachine.value === DraftStates.PREFLIGHT_RUNNING}
                        />
                      ) : null}
                    </div>
                    <div className="flex flex-wrap justify-end items-center gap-2 ml-auto min-w-0">
                      <PanelButtons
                        draftStateMachine={draftStateMachine}
                        hasAccessToStyleGuide={hasAccessToStyleGuide}
                        highQuality={highQuality}
                        setHighQuality={setHighQuality}
                        requesting={requesting}
                        outOfQuota={generationOutOfQuota}
                        proseMode={proseMode}
                        PROSE_MODES={PROSE_MODES}
                        sceneId={sceneId}
                        setScenes={setScenes}
                        prosePreflight={prosePreflight}
                        documentScenes={documentScenes}
                        scene={scene}
                        generateProse={generateProse}
                        setLastInvocation={setLastInvocation}
                        editor={editor}
                        editorRef={editorRef}
                        abortStream={abortStream}
                        recordDraftPause={() => draftPauseTelemetryRef.current?.pause()}
                        setRequesting={setRequesting}
                        setPreflightResponse={setPreflightResponse}
                        lastInvocation={lastInvocation}
                        setProseWords={setProseWords}
                        currentProseMode={currentProseMode}
                      />
                    </div>
                  </div>
                </ButtonContainer>
              </XStateConditionalRenderer>
            </div>
          </HeaderContainer>
          {showPreflight && (
            <PreflightAnalysis
              preflightResponse={preflightResponse}
              documentScenes={documentScenes}
              rewriteScenes={rewriteScenes}
              retryPreflight={prosePreflight}
              proseMode={proseMode}
              hasAccessToPOVAndTenseBeta={hasAccessToPOVAndTenseBeta}
              povTense={effectivePOVTense}
              missingScenesAndNotConnected={missingScenesAndNotConnected}
              isPreflightRunning={draftStateMachine.value === DraftStates.PREFLIGHT_RUNNING}
            >
              <XStateConditionalRenderer
                inclusiveStates={[DraftStates.PREFLIGHT_RUNNING, DraftStates.PREFLIGHT_REVIEW, DraftStates.REWRITING_SCENES]}
                currentState={draftStateMachine.value}
              >
                <PanelButtons
                  draftStateMachine={draftStateMachine}
                  hasAccessToStyleGuide={hasAccessToStyleGuide}
                  highQuality={highQuality}
                  setHighQuality={setHighQuality}
                  requesting={requesting}
                  outOfQuota={generationOutOfQuota}
                  proseMode={proseMode}
                  PROSE_MODES={PROSE_MODES}
                  sceneId={sceneId}
                  setScenes={setScenes}
                  prosePreflight={prosePreflight}
                  documentScenes={documentScenes}
                  scene={scene}
                  generateProse={generateProse}
                  setLastInvocation={setLastInvocation}
                  editor={editor}
                  editorRef={editorRef}
                  abortStream={abortStream}
                  recordDraftPause={() => draftPauseTelemetryRef.current?.pause()}
                  setRequesting={setRequesting}
                  setPreflightResponse={setPreflightResponse}
                  lastInvocation={lastInvocation}
                  setProseWords={setProseWords}
                />
              </XStateConditionalRenderer>
            </PreflightAnalysis>
          )}
          </DraftProseAccessGate>
        </InnerContainer>
      </MainContainer>
    );
  }
);

export default GeneratePanel;
